import React, { useState, useContext, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import AddIcon from '@material-ui/icons/Add';
import CircularProgress from '@material-ui/core/CircularProgress';
import EventIcon from '@material-ui/icons/Event';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { apiGet } from '../../../brainApi';
import { QuickAppointmentContext } from '../../../Context/QuickAppointmentContext';
import { getVehicleType } from '../../../VehicleConstants';
import { restructureVehicleData } from '../../../vehicleHelper';

const ConvertToAppointmentButton = ({
  record,
  menuItemActionsBeforeOpening,
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [quote, setQuote] = useState({});
  const { quote_id } = record;

  const [selectedOptions, setSelectedOptions] = useState({});

  useEffect(() => {
    // When the modal opens, we pull the most up to date quote data from Brain2
    if (isModalOpen) {
      setIsLoading(true);
      apiGet(`/quote-detailed/${quote_id}`).then((detailedQuote) => {
        // Set menu options from most up to date quote info.
        setSelectedOptions(
          detailedQuote?.sections?.reduce((o, currentSection) => {
            o[currentSection.id] = {
              ...currentSection.options?.[0],
              optionIdx: 0,
            };
            return o;
          }, {}),
        );

        // Store quote in state
        setQuote(detailedQuote);
        setIsLoading(false);
      });
    }
  }, [isModalOpen]);
  const {
    handleOpen,
    setServiceData,
    setVehicleData,
    setActiveStep,
    setContactData,
    setCustomerType,
    setExistingData,
    setCouponCode,
    setCustomDiscountAmount,
    setCustomDiscountReason,
    setQuoteId,
  } = useContext(QuickAppointmentContext);

  const handleSelect = (event, sectionId) => {
    setSelectedOptions((prevSelectedOptions) => ({
      ...prevSelectedOptions,
      [sectionId]: event.target.value,
    }));
  };

  const openQuickAppointment = () => {
    const {
      customer,
      quote_request,
      coupon_code,
      custom_discount_amount_dollars,
      custom_discount_reason,
    } = quote;
    const { vehicle, customer_email } = quote_request || {};

    // Convert selected options to a list of services and pass into QuickAppointment
    let services = [];
    Object.values(selectedOptions).forEach((option) => {
      services = services.concat(option?.services);
    });
    setServiceData({
      newServices: services,
      coupon_code,
      custom_discount_amount_dollars,
      custom_discount_reason,
    });

    setCouponCode(coupon_code);
    setCustomDiscountAmount(custom_discount_amount_dollars);
    setCustomDiscountReason(custom_discount_reason);
    setQuoteId(quote_id);

    if (vehicle) {
      setVehicleData({
        vehicle_id: vehicle.id,
        photo_filenames: vehicle.photo_filenames,
        inputMethodType: vehicle.input_method_type,
        [vehicle.input_method_type]: restructureVehicleData(vehicle),
        type: getVehicleType(vehicle),
      });
    }

    if (customer) {
      setCustomerType('existing');
      setContactData(customer);
      setExistingData(customer);
      setActiveStep(1);
    } else if (customer_email) {
      setContactData({
        email: customer_email,
      });
      setActiveStep(0);
    }

    // Close modal
    setModalOpen(false);
    // Open quick appointment
    handleOpen();
  };
  return (
    <>
      {menuItemActionsBeforeOpening ? (
        <MenuItem
          onClick={() => {
            menuItemActionsBeforeOpening(() => setModalOpen(true));
          }}
        >
          <ListItemIcon>
            <EventIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Create Appointment" />
        </MenuItem>
      ) : (
        <Tooltip title="Convert Quote to Appointment">
          <IconButton onClick={() => setModalOpen(true)}>
            <EventIcon />
          </IconButton>
        </Tooltip>
      )}

      <Dialog open={isModalOpen} onClose={() => setModalOpen(false)}>
        <DialogTitle>Convert Quote to Appointment</DialogTitle>
        <DialogContent style={{ width: '500px', padding: '20px' }}>
          <Grid container direction="column" spacing={2}>
            {!isLoading && quote?.sections?.length ? (
              quote.sections.map((section, sectionIdx) => (
                <Grid
                  item
                  container
                  alignItems="center"
                  direction="row"
                  justify="space-between"
                >
                  <Grid item>
                    <strong>
                      {section.name || `Section ${sectionIdx + 1}`}
                    </strong>
                  </Grid>
                  <Grid item>
                    <Select
                      value={selectedOptions[section.id]}
                      displayEmpty
                      onChange={(event) => {
                        handleSelect(event, section.id);
                      }}
                      renderValue={(value) =>
                        value.name || `Option ${value.optionIdx + 1}`
                      }
                    >
                      {section?.options?.map((option, optionIdx) => (
                        <MenuItem value={{ ...option, optionIdx }}>
                          {option.name || `Option ${optionIdx + 1}`}
                        </MenuItem>
                      ))}
                      <MenuItem value={{ services: [], name: 'None' }}>
                        None
                      </MenuItem>
                    </Select>
                  </Grid>
                </Grid>
              ))
            ) : (
              <p>
                {isLoading
                  ? 'Loading quote options...'
                  : 'There are no options attached to this quote'}
              </p>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            onClick={openQuickAppointment}
            variant="contained"
            color="default"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {isLoading ? 'Loading...' : 'Open Quick Appointment'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConvertToAppointmentButton;
